import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { Building2 } from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import AddTenantDepartmentModal from "src/components/modals/AddTenantDepartmentModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantDepartmentActions from "src/redux/slices/tenantDepartmentSlice";
import { TenantDepartment } from "src/types/tenantDepartment.types";
import { toast } from "react-toastify";
import { formatCreatedBy } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";

const TenantDepartmentListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRow, setEditRow] = useState<TenantDepartment | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<TenantDepartment | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const { tenantDepartmentList, deleteTenantDepartment } = useAppSelector(
    (state) => state.tenantDepartment
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreate = isTenantAdmin || (permissions?.users || []).includes("create");
  const canEdit = isTenantAdmin || (permissions?.users || []).includes("edit");
  const canDelete = isTenantAdmin || (permissions?.users || []).includes("delete");
  const dispatch = useAppDispatch();

  const rows: TenantDepartment[] = tenantDepartmentList?.data?.data || [];

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, limit]);

  useEffect(() => {
    if (deleteTenantDepartment.data && deleteRequested) {
      toast.success("Department deleted successfully");
      dispatch(tenantDepartmentActions.resetDeleteTenantDepartment());
      setDeleteRequested(false);
      handleDeleteModalClose();
      getList(1);
    } else if (deleteTenantDepartment.error && deleteRequested) {
      toast.error(`Error: ${deleteTenantDepartment.error}`);
      setDeleteRequested(false);
      handleDeleteModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTenantDepartment, dispatch, deleteRequested]);

  const getList = (page: number) =>
    dispatch(
      tenantDepartmentActions.getTenantDepartmentListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
      })
    );

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
  };
  const hasActiveFilters = !!search;

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteRow(null);
  };
  const onConfirmDelete = () => {
    if (!deleteRow?._id) return;
    setDeleteRequested(true);
    dispatch(tenantDepartmentActions.deleteTenantDepartmentRequest(deleteRow._id));
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditRow(null);
  };
  const handleModalSuccess = () => {
    handleModalClose();
    getList(tenantDepartmentList?.data?.pagination?.currentPage || 1);
  };

  return (
    <DashboardLayout
      meta={{
        title: "Departments | Invyntra",
        description: "Organizational units for staff.",
        keywords: "departments, team, staff, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Building2 size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Departments
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Organizational units for staff (e.g. Sales, Engineering).
              </p>
            </div>
          </div>

          {canCreate && (
            <Button rounded="full" onClick={() => setShowAddModal(true)}>
              <Plus size={16} className="mr-1" />
              Add Department
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search departments..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Departments"
            description="Search and manage your departments."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "name",
                header: "Name",
                accessor: ({ key, row }) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {row[key]}
                  </span>
                ),
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Created",
                accessor: ({ key, row }) => (
                  <span className="text-gray-600 dark:text-gray-300">
                    {formatDateTime(row[key], "DD MMM YYYY")}
                  </span>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) =>
                  !canEdit && !canDelete ? null : (
                    <Popover
                      trigger={
                        <button
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {(close) => (
                        <div className="w-44 py-1.5">
                          {canEdit && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                setEditRow(row);
                                setShowAddModal(true);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                              onClick={() => {
                                close();
                                setDeleteRow(row);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </Popover>
                  ),
              },
            ]}
            data={rows}
            pagination={tenantDepartmentList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={tenantDepartmentList.loading}
          />
        </Card>
      </div>

      {showAddModal && (
        <AddTenantDepartmentModal
          department={editRow}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {showDeleteModal && deleteRow?._id && (
        <ConfirmationModal
          title="Delete department"
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteRow.name}
              </span>
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDelete}
          onCancel={handleDeleteModalClose}
          open={showDeleteModal}
          isLoading={deleteTenantDepartment.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default TenantDepartmentListPage;
